import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { setDragPreviewChip } from "./dragPreviewChip";
import {
  edgeFromPointer,
  isTransformRowDragData,
  TRANSFORM_ROW_DRAG_KIND,
  type TransformRowDragData,
} from "./PipelineNavBar";
import {
  type DropEdge,
  type StagedResource,
  usePipelineStaging,
} from "./pipelineStaging";
import { useStudioPipeline } from "./StudioPipelineProvider";

// Block reorder DnD on the transform tab (Figma 14419-219770): the whole
// stage group — script row plus its SCHEMA sub-section — travels as one
// unit, so schemas ride along with their script. Extracted from
// StageNavPanel.

// semantic/greenbackground isn't exported by design-variables yet —
// inlined from the Figma token.

// Block reorder DnD on the transform tab (Figma 14419-219770): the whole
// stage group — script row plus its SCHEMA sub-section — travels as one
// unit, so schemas ride along with their script. Only the row's handle
// starts a drag (plain clicks keep toggling selection). While a compatible
// drag hovers the group, dropEdge marks the pointer's half for the
// insertion line.
function useBlockReorderDnd(
  groupRef: React.RefObject<HTMLDivElement>,
  handleRef: React.RefObject<HTMLSpanElement>,
  resource: StagedResource,
  enabled: boolean,
) {
  const theme = useTheme();
  const { reorderTransforms } = usePipelineStaging();
  // A view-only studio (editsLocked) reorders nothing.
  const { editsLocked } = useStudioPipeline();
  const [dragging, setDragging] = useState(false);
  const [dropEdge, setDropEdge] = useState<DropEdge | null>(null);
  const { id, name } = resource;

  useEffect(() => {
    if (!enabled || editsLocked) return;
    const group = groupRef.current;
    const handle = handleRef.current;
    if (!group) return;
    if (!handle) return;
    return combine(
      draggable({
        element: group,
        dragHandle: handle,
        getInitialData: (): TransformRowDragData => ({
          kind: TRANSFORM_ROW_DRAG_KIND,
          id,
        }),
        onGenerateDragPreview: ({ nativeSetDragImage }) =>
          setDragPreviewChip({ nativeSetDragImage, theme, name }),
        onDragStart: () => setDragging(true),
        onDrop: () => setDragging(false),
      }),
      dropTargetForElements({
        element: group,
        canDrop: ({ source }) =>
          isTransformRowDragData(source.data) && source.data.id !== id,
        onDrag: ({ location }) =>
          setDropEdge(edgeFromPointer(group, location.current.input.clientY)),
        onDragLeave: () => setDropEdge(null),
        onDrop: ({ source, location }) => {
          setDropEdge(null);
          if (!isTransformRowDragData(source.data)) return;
          if (source.data.id === id) return;
          reorderTransforms(
            source.data.id,
            id,
            edgeFromPointer(group, location.current.input.clientY),
          );
        },
      }),
    );
  }, [
    enabled,
    id,
    name,
    reorderTransforms,
    theme,
    groupRef,
    handleRef,
    editsLocked,
  ]);

  return { dragging, dropEdge };
}

// Stage group wrapper owning the block-reorder refs — the handle ref goes
// back to the caller via render prop so the script row can mount the
// three-bar handle inside its List/item.
export function ReorderableStageGroup({
  item,
  enabled,
  testId,
  children,
}: {
  item: StagedResource;
  enabled: boolean;
  testId: string;
  children: (handleRef: React.RefObject<HTMLSpanElement>) => React.ReactNode;
}) {
  const groupRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLSpanElement>(null);
  const { dragging, dropEdge } = useBlockReorderDnd(
    groupRef,
    handleRef,
    item,
    enabled,
  );
  return (
    <StageGroup
      ref={groupRef}
      data-testid={testId}
      $dragging={dragging}
      $dropEdge={dropEdge}
    >
      {children(handleRef)}
    </StageGroup>
  );
}

// The V2 group separator is a solid borderdark hairline (Figma
// Bridge_PipelineNav/separator in 14397-199487) — unlike the classic nav's
// dashed SectionSeparator.
export const GroupSeparator = styled.div`
  height: 1px;
  flex-shrink: 0;
  background: ${(p) => p.theme.semanticBorderDark};
`;

// Staged-item group wrapper — semantic/greenbackground behind the stage
// row, its SCHEMA sub-section and the closing separator (14397-193694).
// While a block-reorder drag runs, the dragged group dims and a 2px
// mainprimary line marks the insertion edge on hovered groups.
const GREEN_BACKGROUND = "rgba(26, 198, 115, 0.05)";

const StageGroup = styled.div<{
  $dragging?: boolean;
  $dropEdge?: DropEdge | null;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background: ${GREEN_BACKGROUND};
  opacity: ${(p) => (p.$dragging ? 0.4 : 1)};

  ${(p) =>
    p.$dropEdge &&
    `&::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      ${p.$dropEdge === "top" ? "top: -1px;" : "bottom: -1px;"}
      height: 2px;
      background: ${p.theme.semanticMainPrimary};
      z-index: 1;
      pointer-events: none;
    }`}

  /* The first group sits flush under the tab bar — a -1px top line pokes
     into it and gets clipped/covered; pin that one fully inside. */
  ${(p) =>
    p.$dropEdge === "top" &&
    `&:first-child::after {
      top: 0;
    }`}
`;
